import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { LoadingService } from "src/app/components/loading/loading.service";
import { DIALOG_WIDTHS, DialogService } from "src/app/core/core-services/services/dialog.service";
import { AuthService } from "src/app/core/core-services/services/auth.service";
import { DataService } from "src/app/core/core-services/services/dataservices/data.service";
import { WarningDialogComponent } from "src/app/core/core-services/warning-dialog/warning-dialog.component";
import { selectHasFlyLearning } from "src/app/core/store/plan/plan.selectors";
import { ApiResponse } from "src/app/core/models/api.model";
import { userHasRoleplayAccess } from "src/app/core/models/user.model";

/**
 * Opens the FlyLearning and FlyMSG Roleplay companion apps via backend SSO
 * redirects.
 *
 * Owns the entitlement gates for both apps. FlyLearning: the
 * `plans.has_fly_learning` flag (surfaced through `selectHasFlyLearning`)
 * is the single source of truth — never gate FlyLearning on plan
 * slugs/names. Roleplay: `hasRoleplayAccess` below. Previously the menu
 * and admin-layout each carried their own copy of these flows and had
 * drifted apart (admin-layout still slug-matched a hardcoded plan list).
 */
@Injectable({ providedIn: "root" })
export class SsoRedirectService {
  private hasFlyLearning = false;

  constructor(
    private readonly store: Store,
    private readonly dataService: DataService,
    private readonly authService: AuthService,
    private readonly dialog: DialogService,
    private readonly loadingService: LoadingService
  ) {
    this.store.select(selectHasFlyLearning).subscribe((hasFlyLearning) => {
      this.hasFlyLearning = hasFlyLearning;
    });
  }

  /**
   * Opens FlyLearning in a new tab via SSO — or the SalesPro upgrade dialog
   * when the current plan lacks the entitlement.
   */
  openFlyLearning(): void {
    if (!this.hasFlyLearning) {
      this.dialog.openDialogComponent(
        WarningDialogComponent,
        { type: "flyLearningWarning", selectedPlan: "salesPro" },
        DIALOG_WIDTHS.lg
      );
      return;
    }

    const email = this.authService.userDetails?.user_details?.email;
    if (!email) {
      return;
    }

    // Email is encoded on the backend.
    this.dataService
      .post({ url: "v1/authenticateUser", data: { Email: email }, isLoader: true })
      .subscribe((response: ApiResponse<{ redirect_url?: string }>) => {
        const redirectUrl = response?.result?.redirect_url;
        if (redirectUrl) {
          window.open(redirectUrl, "_blank").focus();
        }
      });
  }

  /** Whether the signed-in user may see FlyMSG Roleplay — see {@link userHasRoleplayAccess}. */
  get hasRoleplayAccess(): boolean {
    return userHasRoleplayAccess(this.authService.userDetails?.user_details);
  }

  /**
   * Opens FlyMSG Roleplay in a new tab via its SSO redirect.
   *
   * `targetPath` deep-links into the roleplay app (e.g.
   * `/settings/subscription`): it rides along as the `path` query param,
   * which roleplay's AuthService.initialize() sanitizes and restores after
   * consuming the token handoff.
   */
  openRolePlay(targetPath?: string): void {
    this.loadingService.show();
    this.dataService.get({ url: "v2/role-play/redirect", isLoader: true }).subscribe({
      next: (response: ApiResponse<{ redirect_url?: string }>) => {
        let redirectUrl = response?.result?.redirect_url;
        if (redirectUrl) {
          if (targetPath) {
            const separator = redirectUrl.includes("?") ? "&" : "?";
            redirectUrl = `${redirectUrl}${separator}path=${encodeURIComponent(targetPath)}`;
          }
          window.open(redirectUrl, "_blank").focus();
        }
      },
      // The previous copies hid the spinner only on complete, leaving it
      // stuck when the redirect call failed — hide on both outcomes.
      error: () => this.loadingService.hide(),
      complete: () => this.loadingService.hide(),
    });
  }
}
